'use client';

import { Avatar, AvatarImage } from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Separator } from '@ahafood/ui/components/separator';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import React, { useEffect, useState, type PropsWithChildren } from 'react';
import type { Store } from '@/api/ahafood/get-store-fn';
import { useGetStores } from '@/api/use-get-store';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';

type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

interface StoreCardProps {
  store: Store;
  index: number;
}

interface NewStoreSessionProps {
  cityId: string;
  citySlug: string;
}

export const StoreHours: React.FC<{ openHours: OpenHoursType }> = ({
  openHours,
}) => {
  const getCurrentDayHours = (openHours: OpenHoursType): string => {
    const today = new Date().getDay();
    const dayIndex = today === 0 ? 6 : today - 1;
    const hours = openHours[dayIndex.toString()];

    if (!hours || hours.length === 0) {
      return 'Đóng cửa';
    }

    const formatTime = (minutes: number): string => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    return hours
      .map(
        (range) =>
          `${formatTime(range.start_time)} - ${formatTime(range.end_time)}`
      )
      .join(', ');
  };
  const [currentDayHours, setCurrentDayHours] = useState<string>('');

  useEffect(() => {
    const updateHours = () => {
      setCurrentDayHours(getCurrentDayHours(openHours));
    };

    updateHours();
    const timer = setInterval(updateHours, 600000);

    return () => clearInterval(timer);
  }, [openHours]);

  return (
    <div className="flex flex-row text-sm text-neutral-60">
      <Clock className="mr-1 size-4 self-center" />
      <span className="items-center truncate">{currentDayHours}</span>
    </div>
  );
};

export default function NewStoreSession({
  cityId,
  citySlug,
}: NewStoreSessionProps) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const address = useAiFoodOrderStore.use.address();

  // Fetch new stores
  const { data: storesData, isLoading } = useGetStores({
    city_id: cityId,
    limit: 15,
    special_request: 'new_store',
    ...(address?.lat && address?.lng
      ? {
          lat: address.lat,
          lng: address.lng,
        }
      : {}),
  });

  const stores = storesData?.stores ?? [];

  const handleDealToggle = () => {
    router.push(`/${citySlug}/danh-sach-cua-hang-moi`);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="container py-8 w-full max-w-screen-xl mx-auto">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Don't render if no stores
  if (!stores || stores.length === 0) return null;

  return (
    <StoreListWrapper>
      <FreeModeSwiper
        navigation={false}
        className="w-full !overflow-visible mb-6"
        spaceBetween={isMobile ? 16 : 24}
        pagination
        data-testid="home-new-store-list"
      >
        {stores.map((store, index) => (
          <FreeModeSwiper.Slide
            key={store._id}
            style={{ width: isMobile ? '45%' : 256 }}
          >
            <NewStoreItem store={store} index={index} />
          </FreeModeSwiper.Slide>
        ))}
        {/* {stores.length > 5 && (
          <FreeModeSwiper.Slide style={{ width: isMobile ? '45%' : 256 }}>
            <Card
              onClick={handleDealToggle}
              className="group h-full flex flex-col items-center justify-center bg-white border-none rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer min-h-[280px]"
            >
              <div className="flex flex-col items-center justify-center gap-4 p-6">
                <div className="rounded-full bg-neutral-10 p-6 group-hover:bg-neutral-20 transition-all">
                  <ArrowRight className="size-12 text-neutral-70" />
                </div>
                <p className="text-lg font-semibold text-neutral-80">
                  Xem thêm
                </p>
              </div>
            </Card>
          </FreeModeSwiper.Slide>
        )} */}
      </FreeModeSwiper>

      {/* Bottom View All Button */}
      <div className="mt-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Button
            variant="outline"
            style={{ boxShadow: '0px 4px 4px 1px #0000001F inset' }}
            onClick={() => {
              handleDealToggle();
              trackEvent('click_button', {
                action_time: Math.floor(Date.now() / 1000),
                button_name: 'new_store_click',
                button_location: 'new_store_section',
              });
            }}
            className="w-full h-12 md:h-14 bg-[#EEF7FF] hover:bg-[#EEF7FF] text-blue-50 hover:text-blue-60 font-bold text-sm md:text-base rounded-2xl border-blue-50"
          >
            Xem tất cả quán mới
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </StoreListWrapper>
  );
}

export function NewStoreItem({ store, index }: StoreCardProps) {
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuthStore();

  const handleStoreClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'store_detail',
      button_location: 'new_store_section',
      storeId: store._id,
      storeSlug: store.slug,
      storeName: store.name,
      user_id: user?.cunstomerId,
    });
    router.push(routes.storeDetail(store.slug));
  };

  const imageSrc =
    store.image_url || `${assetPrefix}/static/images/empty-store.webp`;

  return (
    <Card
      onClick={handleStoreClick}
      className="group relative h-full flex flex-col bg-white border-none rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
      data-testid="home-new-store-card"
      data-index={index}
      data-item-id={store._id}
    >
      {/* Image Section */}
      <div className="relative aspect-square overflow-hidden rounded-2xl m-1">
        <Avatar className="size-full rounded-none">
          <AvatarImage
            src={imageSrc || `${assetPrefix}/static/images/empty-store.webp`}
            alt={store.name}
            data-testid="home-new-store-card-image"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Avatar>
      </div>

      {/* Content Section */}
      <div className="px-4 py-3 flex flex-col">
        <h3
          data-testid="home-new-store-card-name"
          className="text-sm font-semibold text-neutral-80 line-clamp-2 mb-1.5 md:text-base h-10 md:h-12"
        >
          {store.name}
        </h3>
        <div className="flex items-center text-xs text-neutral-50 font-normal">
          <Clock className="mr-1 size-3.5" />
          <span className="truncate">
            {(() => {
              const today = new Date().getDay();
              const dayIndex = today === 0 ? 6 : today - 1;
              const hours = store.open_hours[dayIndex.toString()];

              if (!hours || hours.length === 0) {
                return 'Đóng cửa';
              }

              const formatTime = (minutes: number): string => {
                const h = Math.floor(minutes / 60);
                const m = minutes % 60;
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
              };

              const firstSlot = hours[0];
              const lastSlot = hours[hours.length - 1];

              return `${formatTime(firstSlot.start_time)} - ${formatTime(lastSlot.end_time)}`;
            })()}
          </span>
        </div>
      </div>
    </Card>
  );
}

export function StoreListWrapper({ children }: PropsWithChildren) {
  return (
    <section
      className="ahf-deal relative overflow-hidden bg-transparent"
      data-testid="home-new-store-section"
    >
      <div
        className={cn(
          'relative z-[1] mx-auto max-w-screen-xl gap-y-6 px-4 md:px-0 md:py-8'
        )}
      >
        <div
          className="w-full flex items-center justify-center py-6 text-neutral-50 gap-10 bg-transparent"
          data-testid="home-new-store-title"
        >
          <Separator className="h-px w-full flex-1" />
          <span className="font-semibold">Quán mới</span>
          <Separator className="h-px w-full flex-1" />
        </div>
        {children}
      </div>
    </section>
  );
}
